import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, FolderOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { unwrap, extractRows, pickId, PROJECT_ID_KEYS } from "../lib/helpers";
import { KVTable } from "../components/DataViews";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

const NAME_KEYS = ["project_name", "name", "display_name", "title"];

export default function BusinessDetail() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [projectsData, setProjectsData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, p] = await Promise.all([
        api.get(`/dashboard/businesses/${encodeURIComponent(businessId)}`),
        api.get(`/dashboard/businesses/${encodeURIComponent(businessId)}/projects`),
      ]);
      setBusiness(unwrap(b.data));
      setProjectsData(unwrap(p.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    load();
  }, [load]);

  const projects = extractRows(projectsData);

  return (
    <div className="space-y-8" data-testid="business-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate("/")} data-testid="back-to-businesses">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Business Detail</h1>
          <p className="font-data text-xs text-muted-foreground mt-0.5" data-testid="business-id-label">{businessId}</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <section className="space-y-3">
          <h2 className="text-base md:text-lg font-display font-bold tracking-tight">Profile</h2>
          <KVTable data={business} testId="business-profile-table" />
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-base md:text-lg font-display font-bold tracking-tight">
          Projects {!loading && <span className="text-muted-foreground font-normal">({projects.length})</span>}
        </h2>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="p-8 text-sm text-muted-foreground border border-dashed border-border rounded-md" data-testid="projects-empty">
            No projects found for this business.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="projects-grid">
            {projects.map((p, i) => {
              const pid = pickId(p, PROJECT_ID_KEYS);
              const name = pickId(p, NAME_KEYS) || pid || `Project ${i + 1}`;
              return (
                <button
                  key={i}
                  data-testid={`project-card-${pid ?? i}`}
                  disabled={!pid}
                  onClick={() => navigate(`/businesses/${encodeURIComponent(businessId)}/projects/${encodeURIComponent(pid)}`)}
                  className="text-left border border-border rounded-md bg-card p-5 h-full transition-all duration-150 hover:-translate-y-0.5 hover:shadow-md hover:border-blue-800/40 group disabled:opacity-50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <FolderOpen className="h-5 w-5 text-blue-800 dark:text-blue-400" weight="bold" />
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="mt-3 font-display font-bold tracking-tight truncate">{String(name)}</div>
                  {pid && <div className="mt-1 font-data text-[11px] text-muted-foreground truncate">{String(pid)}</div>}
                  {p.status !== undefined && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">{String(p.status)}</div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
